import React from 'react';
import { useTranslation } from 'react-i18next';
import { FlowStep } from '../types';
import { cn } from "@/lib/utils";
import {
  Trash2,
  Shuffle,
  Percent,
  Globe,
  Users,
  Eye,
  Zap,
  Flag,
  ArrowDown,
  TrendingUp,
  Pencil,
  LogIn,
  Box,
  ExternalLink,
  Target,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { STEP_ICONS, STEP_BG_COLORS, STEP_BORDER_COLORS, STEP_TYPE_LABELS } from '../constants/stepStyles';
import { formatCondition } from '../constants/conditionFormatters';
import { getIntegrationMetadata } from '@/lib/integrations-constants';

interface StepBoxWithVariantsProps {
  step: FlowStep; // The step (can be standard or split)
  isSelected?: boolean;
  onClick?: () => void;
  onDelete?: () => void;
  onTransformToSplit?: () => void;
  onEdit?: () => void;
  productionDomains?: string[]; // List of active production domains
  urlViewMode?: 'staging' | 'production';
  productionConfig?: {
    steps?: Array<{ id: string; config?: { url?: string }; variants?: Array<{ config?: { url?: string } }> }>;
  }; // Production config to get production URLs
  stepAnalytics?: {
    visitors: number;
    conversions: number;
    progressionRate: number | null;
    progressedToNext: number;
    realtimeVisitors: number;
    conversionValue: number;
    conversionValueUnit: string;
    avgConversionValue: number;
    hasNextStep: boolean;
  };
  variantAnalytics?: Record<
    string,
    {
      visitors: number;
      conversions: number;
      progressionRate: number | null;
      progressedToNext: number;
      realtimeVisitors: number;
      conversionValue: number;
      conversionValueUnit: string;
      avgConversionValue: number;
      hasNextStep: boolean;
    }
  >;
}

// Border and hover colors derived from centralized colors
const STEP_COLORS: Record<string, string> = {
  landing: 'border-blue-500 hover:bg-blue-500/10',
  checkout: 'border-green-500 hover:bg-green-500/10',
  offer: 'border-purple-500 hover:bg-purple-500/10',
  thankyou: 'border-pink-500 hover:bg-pink-500/10',
  custom: 'border-slate-500 hover:bg-slate-500/10',
  upsell: 'border-emerald-500 hover:bg-emerald-500/10',
  downsell: 'border-amber-500 hover:bg-amber-500/10',
  error: 'border-red-500 hover:bg-red-500/10',
  external: 'border-orange-500 hover:bg-orange-500/10',
  shopify: 'border-teal-500 hover:bg-teal-500/10',
  woocommerce: 'border-violet-500 hover:bg-violet-500/10',
};

// Professional neutral tones - no colorful gradients
const VARIANT_BADGE_GRADIENTS: Record<string, string> = {
  landing: 'from-slate-600 to-slate-700',
  checkout: 'from-slate-600 to-slate-700',
  offer: 'from-slate-600 to-slate-700',
  thankyou: 'from-slate-600 to-slate-700',
  custom: 'from-slate-600 to-slate-700',
  upsell: 'from-slate-600 to-slate-700',
  downsell: 'from-slate-600 to-slate-700',
  error: 'from-slate-600 to-slate-700',
  shopify: 'from-slate-600 to-slate-700',
  woocommerce: 'from-slate-600 to-slate-700',
};

// Subtle borders
const VARIANT_BORDER_COLORS: Record<string, string> = {
  landing: 'border-slate-300',
  checkout: 'border-slate-300',
  offer: 'border-slate-300',
  thankyou: 'border-slate-300',
  custom: 'border-slate-300',
  upsell: 'border-slate-300',
  downsell: 'border-slate-300',
  error: 'border-slate-300',
  shopify: 'border-slate-300',
  woocommerce: 'border-slate-300',
};

// Note: STEP_ICONS is now imported from ../constants/stepStyles
// Note: formatCondition is now imported from ../constants/conditionFormatters

// Helper component for displaying step URLs (staging or production)
interface StepUrlDisplayProps {
  url: string;
  productionUrl?: string; // Actual production URL from productionConfig
  productionDomains?: string[];
  urlViewMode?: 'staging' | 'production';
}

function StepUrlDisplay({ url, productionUrl, productionDomains, urlViewMode }: StepUrlDisplayProps) {
  const getLiveUrl = () => {
    if (urlViewMode === 'staging') return null;

    // ✅ Use actual production URL if available (respects path remapping)
    if (productionUrl) return productionUrl;

    // ⚠️ Fallback: construct from staging URL (may be incorrect with path remapping)
    if (!productionDomains?.length) return null;
    try {
      const urlObj = new URL(url);
      return `https://${productionDomains[0]}${urlObj.pathname}${urlObj.search}`;
    } catch {
      return null;
    }
  };

  const liveUrl = getLiveUrl();
  const displayUrl = liveUrl || url;
  const isLive = !!liveUrl;

  return (
    <div className="flex items-center gap-2">
      <div className="min-w-0 flex-1">
        <a
          href={displayUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'flex items-center gap-2 truncate text-xs font-medium hover:underline',
            isLive
              ? 'text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300'
              : 'text-primary hover:text-primary dark:text-primary dark:hover:text-primary',
          )}
          title={isLive ? `Live: ${displayUrl}` : `Staging: ${displayUrl}`}
        >
          {isLive ? (
            <Globe className="h-3.5 w-3.5 flex-shrink-0" />
          ) : (
            <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
          )}
          <span className="truncate">{displayUrl.replace(/^https?:\/\//, '')}</span>
        </a>
      </div>
      {isLive && (
        <Badge variant="outline" className="h-4 border-green-200 bg-green-50 px-1 text-[9px] text-green-600">
          Live
        </Badge>
      )}
    </div>
  );
}

/**
 * Step box that displays either:
 * 1. Standard step (single config)
 * 2. Split step (multiple A/B variant configs)
 *
 * Rows = Different conditional paths (different when)
 * Within a step box = A/B config variants (same when, different configs)
 */
export function StepBoxWithVariants({
  step,
  isSelected,
  onClick,
  onDelete,
  onTransformToSplit,
  onEdit,
  productionDomains,
  urlViewMode = 'production', // Default to production if not specified (legacy behavior)
  productionConfig,
  stepAnalytics,
  variantAnalytics,
}: StepBoxWithVariantsProps) {
  const { t } = useTranslation('storefront');
  const Icon = STEP_ICONS[step.type];
  const isSplit = !!step.variants && step.variants.length > 0;
  const condition = step.conditions.when ? formatCondition(step.conditions.when) : null;

  // Get integration logo for storefront types
  const integrationLogo = React.useMemo(() => {
    if (step.type === 'storefront_shopify') {
      return getIntegrationMetadata('shopify')?.logoUrl;
    } else if (step.type === 'storefront_woocommerce') {
      return getIntegrationMetadata('woocommerce')?.logoUrl;
    }
    return null;
  }, [step.type]);

  // Check if step has a config to edit (either standard step or split step with at least one variant)
  const hasConfigToEdit = isSplit
    ? step.variants && step.variants.length > 0 && (step.variants[0].config?.instanceId || step.variants[0].config?.routeId)
    : (step.config?.instanceId || step.config?.routeId);

  // Get production URL for this step from productionConfig
  const getProductionUrl = (): string | undefined => {
    if (!productionConfig?.steps) return undefined;
    const productionStep = productionConfig.steps.find((s: any) => s.id === step.id);
    if (!productionStep) return undefined;

    // For split steps, get URL from first variant
    if (productionStep.variants && productionStep.variants.length > 0) {
      return productionStep.variants[0].config?.url;
    }

    // For standard steps
    return productionStep.config?.url;
  };

  const productionUrl = getProductionUrl();

  return (
    <div className="group/step relative" data-step-box>
      <div
        onClick={onClick}
        className={cn(
          'group relative flex w-full flex-col overflow-hidden rounded-xl border-2 bg-white shadow-md transition-all hover:-translate-y-0.5 hover:shadow-xl dark:bg-slate-900',
          STEP_COLORS[step.type],
          isSelected && 'ring-primary/40 -translate-y-0.5 shadow-xl ring-2 ring-offset-4',
          // Special styling for conversion goal steps (blue accent)
          step.isConversion &&
            'shadow-primary/10 ring-2 ring-primary/30 dark:shadow-primary/10 dark:ring-primary/30',
          // Special styling for entry steps (green accent)
          (step.conditions.entry || step.isDefault) &&
            'shadow-green-100 ring-2 ring-green-400/30 dark:shadow-green-900/20 dark:ring-green-500/30',
          'cursor-pointer',
        )}
      >
        {/* Header: Icon + Title + Type Badge */}
        <div
          className={cn(
            'flex items-center gap-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white px-3.5 py-3 dark:border-slate-700 dark:from-slate-800 dark:to-slate-900',
            // Special header styling for conversion goal steps
            step.isConversion &&
              'bg-gradient-to-r from-primary/5 to-white dark:from-primary/10 dark:to-slate-900',
            // Special header styling for entry steps
            (step.conditions.entry || step.isDefault) &&
              'bg-gradient-to-r from-green-50 to-white dark:from-green-950/20 dark:to-slate-900',
          )}
        >
          {/* Icon */}
          <div
            className={cn(
              'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg shadow-lg',
              integrationLogo
                ? `border-[3px] bg-white ${STEP_BORDER_COLORS[step.type]}`
                : `text-white ${STEP_BG_COLORS[step.type]}`,
            )}
          >
            {integrationLogo ? (
              <img src={integrationLogo} alt={step.type} className="h-5 w-5 object-contain" />
            ) : (
              <Icon className="h-5 w-5" />
            )}
          </div>

          {/* Title + Type Badge */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <h4 className="truncate text-sm font-bold text-slate-900 dark:text-slate-100">{step.label}</h4>
              {/* Inline indicator icons */}
              {(step.conditions.entry || step.isDefault) && (
                <span className="text-sm" title="Entry Point">
                  🚪
                </span>
              )}
              {step.isConversion && <Target className="h-3.5 w-3.5 text-primary dark:text-primary" />}
            </div>
            <div className="mt-1 flex items-center gap-1.5">
              <Badge
                variant="outline"
                className="h-5 border-slate-300 px-2 text-[10px] font-medium capitalize dark:border-slate-600"
              >
                {STEP_TYPE_LABELS[step.type]}
              </Badge>
              {isSplit && (
                <Badge className="h-5 border border-purple-200 bg-purple-100 px-2 text-[10px] font-semibold text-purple-700 shadow-sm dark:border-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                  A/B Test
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Badges Row: Entry + Condition + Priority */}
        <div className="border-b border-slate-200 bg-slate-50/50 px-3 py-2.5 dark:border-slate-700 dark:bg-slate-800/50">
          <div className="flex flex-wrap gap-2">
            {/* Entry Badge */}
            {(step.conditions.entry || step.isDefault) && (
              <Badge className="flex h-6 items-center gap-1 border border-green-200 bg-green-100 px-2 text-[10px] font-semibold text-green-800 shadow-sm dark:border-green-800 dark:bg-green-900/30 dark:text-green-200">
                <span className="text-[12px]">🚪</span>
                Entry
                {step.conditions.entrySource?.platform &&
                  step.conditions.entrySource.platform !== 'direct' && (
                    <span className="ml-1 text-[9px]">({step.conditions.entrySource.platform})</span>
                  )}
              </Badge>
            )}

            {/* Conversion Goal Badge - hidden for now, not used
            {step.isConversion && (
              <Badge className="flex h-6 items-center gap-1 border border-primary/30 bg-primary/10 px-2 text-[10px] font-semibold text-primary shadow-sm dark:border-primary/30 dark:bg-primary/10 dark:text-primary">
                <Target className="h-3 w-3" />
                Conversion Goal
              </Badge>
            )}
            */}

            {/* Condition Badge */}
            {condition && (
              <Badge
                variant="outline"
                className="h-6 gap-1 border-slate-300 bg-white px-2 text-[10px] shadow-sm dark:border-slate-600 dark:bg-slate-900"
              >
                <span className="text-[11px]">{condition.icon}</span>
                {condition.label}
                {condition.detail && <span className="ml-0.5 font-mono">:{condition.detail}</span>}
              </Badge>
            )}

            {/* Priority Badge */}
            {step.conditions.priority !== undefined && (
              <Badge
                variant="secondary"
                className="h-6 border border-slate-300 px-2 text-[10px] font-medium shadow-sm"
              >
                P{step.conditions.priority}
              </Badge>
            )}
          </div>
        </div>

        {/* Analytics Bar - For non-split steps */}
        {!isSplit && stepAnalytics && (
          <div className="border-t-2 border-slate-200 bg-slate-50 px-2.5 py-2 dark:border-slate-700 dark:bg-slate-900">
            <div className="flex items-center justify-between gap-3">
              {/* Left: Traffic Metrics */}
              <div className="flex items-center gap-2.5">
                {/* Realtime - Always show, with visual indicator only when active */}
                <div className="flex items-center gap-1">
                  {stepAnalytics.realtimeVisitors > 0 ? (
                    <>
                      <div className="relative flex h-4 w-4 items-center justify-center">
                        <div className="absolute h-2.5 w-2.5 animate-ping rounded-full bg-slate-400 opacity-75" />
                        <Zap className="relative h-3 w-3 text-slate-600" />
                      </div>
                      <span className="text-xs font-bold text-slate-700">
                        {stepAnalytics.realtimeVisitors}
                      </span>
                    </>
                  ) : (
                    <>
                      <Zap className="h-3 w-3 text-slate-300" />
                      <span className="text-xs font-medium text-slate-400">0</span>
                    </>
                  )}
                </div>

                <div className="h-3 w-px bg-slate-300" />

                {/* Total Visitors - Always show */}
                <div className="flex items-center gap-1">
                  <Users className="h-3 w-3 text-slate-500" />
                  <span className="text-xs font-bold text-slate-900">
                    {stepAnalytics.visitors >= 1000
                      ? `${(stepAnalytics.visitors / 1000).toFixed(1)}k`
                      : stepAnalytics.visitors.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Right: Progression Rate - Only show if step has next step */}
              {stepAnalytics.hasNextStep && (
                <div className="flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5 shadow-sm dark:border-slate-600 dark:bg-slate-800">
                  <TrendingUp className="h-3 w-3 text-slate-500 dark:text-slate-400" />
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-200">
                    {stepAnalytics.progressionRate !== null
                      ? `${stepAnalytics.progressionRate.toFixed(1)}%`
                      : 'N/A'}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Content: Config or Variants */}
        {isSplit ? (
          /* Split Variants - Professional neutral tones */
          <div className="space-y-1.5 border-t bg-slate-50 px-2 pb-2 pt-2 dark:border-slate-700 dark:bg-slate-800">
            {step.variants!.map((variant, idx) => {
              // Get real analytics for this variant
              // ✅ Look up by variant.id (e.g., "step_xxx_variant_0"), NOT instanceId
              const variantMetrics = variantAnalytics?.[variant.id] ||
                stepAnalytics || {
                  visitors: 0,
                  realtimeVisitors: 0,
                  progressionRate: null,
                  progressedToNext: 0,
                  conversions: 0,
                  conversionValue: 0,
                  conversionValueUnit: 'USD',
                  avgConversionValue: 0,
                  hasNextStep: false,
                };
              return (
                <div
                  key={variant.id}
                  className="overflow-hidden rounded-lg border-2 border-slate-200 bg-white shadow-md dark:border-slate-700 dark:bg-slate-800"
                >
                  {/* Variant Header */}
                  <div className="bg-slate-50 px-2 py-2 dark:bg-slate-900">
                    <div className="mb-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {/* Letter Badge - Professional gray */}
                        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-slate-600 shadow">
                          <span className="text-xs font-bold text-white">
                            {String.fromCharCode(65 + idx)}
                          </span>
                        </div>

                        {/* Variant Label */}
                        <span className="text-sm font-bold text-slate-900 dark:text-slate-100">
                          {variant.label}
                        </span>
                      </div>

                      {/* Traffic Badge - Professional gray */}
                      {step.splitType === 'geo' && variant.regions && variant.regions.length > 0 ? (
                        <span className="rounded-md border border-slate-300 bg-slate-100 px-2 py-1 font-mono text-[10px] font-bold text-slate-700 shadow-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200">
                          {variant.regions.slice(0, 2).join(', ')}
                          {variant.regions.length > 2 && ` +${variant.regions.length - 2}`}
                        </span>
                      ) : (
                        <span className="rounded-md border border-slate-300 bg-slate-600 px-2.5 py-1 text-xs font-bold text-white shadow-sm dark:border-slate-500 dark:bg-slate-400 dark:text-slate-900">
                          {variant.percentage}%
                        </span>
                      )}
                    </div>

                    {/* Plugin Details - Clear pills */}
                    <div className="flex items-center gap-1.5">
                      {/* Plugin ID */}
                      <div className="flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5 shadow-sm dark:border-slate-600 dark:bg-slate-700">
                        <Box className="h-2.5 w-2.5 text-slate-500 dark:text-slate-400" />
                        <span className="text-[10px] font-semibold text-slate-900 dark:text-slate-100">
                          {variant.config?.pluginId || t('stepBox.noPlugin')}
                        </span>
                      </div>

                      <span className="text-slate-300 dark:text-slate-600">•</span>

                      {/* Version */}
                      <div className="rounded-md border border-slate-200 bg-white px-2 py-0.5 font-mono text-[10px] font-medium text-slate-700 shadow-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200">
                        {variant.config?.instanceVersion ? `v${variant.config.instanceVersion}` : 'v?'}
                      </div>

                      <span className="text-slate-300 dark:text-slate-600">•</span>

                      {/* Config Name */}
                      <div className="flex-1 truncate rounded-md border border-slate-200 bg-slate-100 px-2 py-0.5 text-center text-[10px] font-semibold text-slate-800 shadow-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200">
                        {variant.config?.variant || 'default'}
                      </div>
                    </div>
                  </div>

                  {/* Variant Metrics */}
                  <div className="border-t-2 border-slate-200 bg-slate-50 px-2.5 py-2 dark:border-slate-700 dark:bg-slate-900">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        {/* Real-time */}
                        <div className="flex items-center gap-1">
                          {variantMetrics.realtimeVisitors > 0 ? (
                            <>
                              <div className="relative flex h-5 w-5 items-center justify-center">
                                <div className="absolute h-3 w-3 animate-ping rounded-full bg-slate-400 opacity-75" />
                                <Zap className="relative h-3.5 w-3.5 text-slate-600" />
                              </div>
                              <span className="text-xs font-bold text-slate-700">
                                {variantMetrics.realtimeVisitors}
                              </span>
                            </>
                          ) : (
                            <>
                              <Zap className="h-3.5 w-3.5 text-slate-300" />
                              <span className="text-xs font-medium text-slate-400">0</span>
                            </>
                          )}
                        </div>

                        <div className="h-4 w-px bg-slate-300" />

                        {/* Total Visitors */}
                        <div className="flex items-center gap-1">
                          <Users className="h-3.5 w-3.5 text-slate-500" />
                          <span className="text-xs font-bold text-slate-900">
                            {variantMetrics.visitors >= 1000
                              ? `${(variantMetrics.visitors / 1000).toFixed(1)}k`
                              : variantMetrics.visitors.toLocaleString()}
                          </span>
                        </div>
                      </div>

                      {/* Right: Progression Rate - Only show if step has next step */}
                      {variantMetrics.hasNextStep && (
                        <div className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1 shadow-sm dark:border-slate-600 dark:bg-slate-800">
                          <TrendingUp className="h-3.5 w-3.5 text-slate-500 dark:text-slate-400" />
                          <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                            {variantMetrics.progressionRate !== null
                              ? `${variantMetrics.progressionRate.toFixed(1)}%`
                              : 'N/A'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Standard Step - Plugin Info with consistent pills */
          step.config?.pluginId && (
            <div className="border-t bg-slate-50 px-2 py-1.5 dark:border-slate-700 dark:bg-slate-800">
              <div className="flex items-center gap-1">
                {/* Plugin ID */}
                <div className="flex items-center gap-0.5 rounded-md border border-slate-200 bg-white px-2 py-0.5 shadow-sm dark:border-slate-600 dark:bg-slate-700">
                  <Box className="h-2.5 w-2.5 text-slate-500 dark:text-slate-400" />
                  <span className="text-[10px] font-semibold text-slate-900 dark:text-slate-100">
                    {step.config.pluginId}
                  </span>
                </div>

                {step.config.instanceVersion && (
                  <>
                    <span className="text-slate-300 dark:text-slate-600">•</span>
                    <div className="rounded-md border border-slate-200 bg-white px-2 py-0.5 font-mono text-[10px] font-medium text-slate-700 shadow-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200">
                      v{step.config.instanceVersion}
                    </div>
                  </>
                )}

                {step.config.variant && (
                  <>
                    <span className="text-slate-300 dark:text-slate-600">•</span>
                    <div className="rounded-md border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-800 shadow-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200">
                      {step.config.variant}
                    </div>
                  </>
                )}
              </div>
            </div>
          )
        )}

        {/* URL/Path Info - Mount Point + Path + Access Link */}
        <div className="border-t-2 border-slate-200 bg-white px-2.5 py-2 dark:border-slate-700 dark:bg-slate-950">
          {isSplit ? (
            // Split step - show first variant's URL
            step.variants && step.variants.length > 0 && step.variants[0].config?.url ? (
              <StepUrlDisplay
                url={step.variants[0].config.url}
                productionUrl={productionUrl}
                productionDomains={productionDomains}
                urlViewMode={urlViewMode}
              />
            ) : (
              <div className="text-xs text-slate-400 dark:text-slate-600">{t('stepBox.noUrlConfigured')}</div>
            )
          ) : // Standard step
          step.config?.url ? (
            <StepUrlDisplay
              url={step.config.url}
              productionUrl={productionUrl}
              productionDomains={productionDomains}
              urlViewMode={urlViewMode}
            />
          ) : (
            <div className="text-xs text-slate-400 dark:text-slate-600">{t('stepBox.noUrlConfigured')}</div>
          )}
        </div>

        {/* Flags - Only if needed, bottom corner */}
        {step.conditions.isDefault && (
          <div className="flex gap-1 border-t px-2 py-1">
            <Badge variant="default" className="h-4 px-1.5 text-[9px]">
              Default
            </Badge>
          </div>
        )}
      </div>

      {/* Floating Action Bar - Appears on hover below card */}
      <div className="absolute -bottom-12 right-0 z-20 -translate-y-2 opacity-0 transition-all duration-300 ease-out group-hover/step:translate-y-0 group-hover/step:opacity-100">
        <div className="flex items-center gap-0.5 rounded-lg border border-slate-300 bg-white p-0.5 shadow-2xl dark:border-slate-600 dark:bg-slate-800">
          {/* Edit Config (hidden for storefront nodes) */}
          {onEdit && hasConfigToEdit && !step.type.startsWith('storefront_') && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="hover:bg-primary/10 hover:text-primary flex h-9 items-center gap-1.5 rounded-md px-3 transition-all"
              title={t('stepBox.editDesign')}
            >
              <Pencil className="h-3.5 w-3.5" />
              <span className="text-xs font-semibold">{t('stepBox.editDesign')}</span>
            </button>
          )}

          {/* Separator */}
          {onEdit && hasConfigToEdit && !step.type.startsWith('storefront_') && (onTransformToSplit || onDelete) && (
            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700" />
          )}

          {/* Split Test - Create or Manage */}
          {onTransformToSplit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onTransformToSplit();
              }}
              className="flex h-9 items-center gap-1.5 rounded-md px-3 transition-all hover:bg-purple-50 hover:text-purple-700 dark:hover:bg-purple-950 dark:hover:text-purple-300"
              title={isSplit ? t('stepBox.manageSplitTest') : t('stepBox.createSplitTest')}
            >
              <Shuffle className="h-3.5 w-3.5" />
              <span className="text-xs font-semibold">{isSplit ? t('stepBox.manage') : t('stepBox.split')}</span>
            </button>
          )}

          {/* Separator */}
          {onTransformToSplit && onDelete && <div className="h-6 w-px bg-slate-200 dark:bg-slate-700" />}

          {/* Delete */}
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="flex h-9 items-center gap-1.5 rounded-md px-3 transition-all hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950 dark:hover:text-red-400"
              title={t('stepBox.deleteStep')}
            >
              <Trash2 className="h-3.5 w-3.5" />
              <span className="text-xs font-semibold">{t('stepBox.remove')}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
